/**
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: Apache-2.0.
 */
import { useEffect, useCallback } from 'react';
import { mqtt5, auth, iot } from 'aws-iot-device-sdk-v2';
import { once } from 'events';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { toUtf8 } from '@aws-sdk/util-utf8-browser';

import {
  AWS_REGION,
  AWS_COGNITO_IDENTITY_POOL_ID,
  AWS_IOT_ENDPOINT,
} from './iot-settings';

function log(msg) {
  let now = new Date();
  console.log(`${now.toString()}: ${msg}`);
}

class AWSCognitoCredentialsProvider extends auth.CredentialsProvider {
  constructor(options, expire_interval_in_ms) {
    super();
    this.options = options;

    setInterval(async () => {
      await this.refreshCredentials();
    }, expire_interval_in_ms ?? 3600 * 1000);
  }

  getCredentials() {
    return {
      aws_access_id: this.cachedCredentials?.accessKeyId ?? '',
      aws_secret_key: this.cachedCredentials?.secretAccessKey ?? '',
      aws_sts_token: this.cachedCredentials?.sessionToken,
      aws_region: this.options.Region,
    };
  }

  async refreshCredentials() {
    log('Fetching Cognito credentials');
    this.cachedCredentials = await fromCognitoIdentityPool({
      identityPoolId: this.options.IdentityPoolId,
      clientConfig: { region: this.options.Region },
    })();
  }
}

function Mqtt5Connector({ liftTelemetryPayload, devicename }) {
  const qos0Topic = `telemetry/${devicename}`;

  const createClient = useCallback(
    (provider) => {
      let wsConfig = {
        credentialsProvider: provider,
        region: AWS_REGION,
      };

      let builder =
        iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
          AWS_IOT_ENDPOINT,
          wsConfig
        );

      let client = new mqtt5.Mqtt5Client(builder.build());

      client.on('error', (error) => {
        log('Error event: ' + error.toString());
      });

      client.on('messageReceived', (eventData) => {
        if (eventData.message.payload) {
          liftTelemetryPayload(
            JSON.parse(toUtf8(eventData.message.payload)),
            devicename
          );
        }
      });

      client.on('attemptingConnect', (eventData) => {
        log('Attempting Connect event');
      });

      client.on('connectionSuccess', (eventData) => {
        log('Connection Success event');
        log('Connack: ' + JSON.stringify(eventData.connack));
        log('Settings: ' + JSON.stringify(eventData.settings));
      });

      client.on('connectionFailure', (eventData) => {
        log('Connection failure event: ' + eventData.error.toString());
      });

      client.on('disconnection', (eventData) => {
        log('Disconnection event: ' + eventData.error.toString());
        if (eventData.disconnect !== undefined) {
          log('Disconnect packet: ' + JSON.stringify(eventData.disconnect));
        }
      });

      client.on('stopped', (eventData) => {
        log('Stopped event');
      });

      return client;
    },
    [devicename, liftTelemetryPayload]
  );

  const testSuccessfulConnection = useCallback(async () => {
    var client;
    /** Set up the credentialsProvider */
    const provider = new AWSCognitoCredentialsProvider({
      IdentityPoolId: AWS_COGNITO_IDENTITY_POOL_ID,
      Region: AWS_REGION,
    });
    /** Make sure the credential provider fetched before setup the connection */
    await provider.refreshCredentials();

    client = createClient(provider);

    const attemptingConnect = once(client, 'attemptingConnect');
    const connectionSuccess = once(client, 'connectionSuccess');

    client.start();

    await attemptingConnect;
    await connectionSuccess;

    const suback = await client.subscribe({
      subscriptions: [{ qos: mqtt5.QoS.AtMostOnce, topicFilter: qos0Topic }],
    });
    log('Suback result: ' + JSON.stringify(suback));
  }, [qos0Topic, createClient]);

  useEffect(() => {
    testSuccessfulConnection(); //initial execution
  }, [testSuccessfulConnection]);
}

export default Mqtt5Connector;
